import { ColDef, ColumnResizedEvent, GridApi } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { ManagedTickerData } from '../../../apis/ticker-management/ticker-management-api.def.ts'
import { CheckOutlined, EditOutlined } from '@ant-design/icons'
import { GridReadyEvent, ICellRendererParams } from 'ag-grid-enterprise'
import React, { useCallback, useMemo, useRef } from 'react'
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react'
import { TICKER_MANAGEMENT_COLUMN_STATE_KEY } from '../../../utils/constants.ts'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { selectManagementInput } from '../../../redux/slices/management-input.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useTranslation } from 'react-i18next'
import { useIsSuperViewer } from '../../../common/hooks/use-is-superviewer.ts'
import {
  getOrderedColumnsFromState,
  isPersistableColumnResize,
} from '../../../utils/ag-grid-table-helpers.ts'

const BooleanCellRenderer: React.FC<ICellRendererParams> = (props) => {
  return <span>{props.value ? <CheckOutlined /> : ''}</span>
}

interface TickerManagementTableProps {
  isLoading: boolean
  isFetching: boolean
  tickerManagementList: ManagedTickerData[]
  onEditTicker: (id: string) => void
  height: number
}

export const TickerManagementTable: React.FC<TickerManagementTableProps> = ({
  isLoading,
  isFetching,
  tickerManagementList,
  onEditTicker,
  height,
}) => {
  const gridApiRef = useRef<GridApi | null>(null)
  const gridRef = useRef<AgGridReact>(null)
  const { t } = useTranslation()
  const managementInput = useAppSelector(selectManagementInput)
  const isSuperViewer = useIsSuperViewer()

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      ...(!managementInput.expiredTicker && !isSuperViewer
        ? [
            {
              headerName: '',
              field: 'id',
              width: 49,
              cellRenderer: (props: CustomCellRendererProps) => {
                return (
                  <IdeyaButton
                    icon={
                      <EditOutlined
                        onClick={() => onEditTicker(props.data.id)}
                      />
                    }
                  />
                )
              },
            },
          ]
        : []),
      {
        headerName: t('ticker_management.table.columns.ticker_name'),
        field: 'name',
      },
      {
        headerName: t('ticker_management.table.columns.bbg_yellow_key'),
        field: 'bbgYellowKey',
      },
      {
        headerName: t('ticker_management.table.columns.figi'),
        field: 'figi',
      },
      {
        headerName: t('ticker_management.table.columns.ric'),
        field: 'ric',
      },
      {
        headerName: t('ticker_management.table.columns.category'),
        field: 'region',
      },
      {
        headerName: t('ticker_management.table.columns.can_long'),
        field: 'canLong',
        cellRenderer: BooleanCellRenderer,
      },
      {
        headerName: t('ticker_management.table.columns.can_short'),
        field: 'canShort',
        cellRenderer: BooleanCellRenderer,
      },
      {
        headerName: t('ticker_management.table.columns.state'),
        field: 'status',
      },
      {
        headerName: t('ticker_management.table.columns.start_date'),
        field: 'startDate',
      },
      {
        headerName: t('ticker_management.table.columns.end_date'),
        field: 'endDate',
      },
      {
        headerName: t('ticker_management.table.columns.is_adr'),
        field: 'isAdr',
        cellRenderer: BooleanCellRenderer,
      },
    ]

    const savedState = localStorage.getItem(TICKER_MANAGEMENT_COLUMN_STATE_KEY)
    const orderedColumns = getOrderedColumnsFromState(
      savedState,
      baseColumns,
      gridApiRef.current
    )
    if (orderedColumns) return orderedColumns

    return baseColumns
  }, [managementInput.expiredTicker, onEditTicker, t])

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
    if (!gridRef.current) return
    const savedState = localStorage.getItem(TICKER_MANAGEMENT_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }, [])

  const handleColumnChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      TICKER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleSortChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      TICKER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleColumnResized = (event: ColumnResizedEvent) => {
    // Persist manual drag and the column menu's autosize actions.
    if (!gridRef.current || !isPersistableColumnResize(event)) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      TICKER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: false,
      initialWidth: 200,
      mainMenuItems: (params) => {
        return params.defaultItems.map((item) => {
          if (item === 'resetColumns') {
            return {
              name: 'Reset Columns',
              action: () => {
                if (localStorage.getItem(TICKER_MANAGEMENT_COLUMN_STATE_KEY)) {
                  localStorage.removeItem(TICKER_MANAGEMENT_COLUMN_STATE_KEY)
                }

                gridRef.current?.api.resetColumnState()
              },
            }
          }
          return item
        })
      },
    }),
    []
  )

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={tickerManagementList}
      loading={isLoading || isFetching}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onColumnResized={handleColumnResized}
      onSortChanged={handleSortChanged}
      height={height}
    />
  )
}
